/**
 * Add Child flow launched from the inbox's selection bar — the same chooser,
 * New Conversation form (child mode) and link-existing search the
 * conversation view opens from its Parent/Child tab, so both entry points
 * behave identically. The parent's details are fetched on open because the
 * inbox row doesn't carry description/client/campaign/team/priority.
 */
import { useEffect, useState, type JSX } from 'react'
import { api } from '../../api/client.js'
import { useToast } from '../system/ToastProvider.js'
import ChildTicketDrawer from './detail/ChildTicketDrawer.js'
import CreateConversationPanel, { type ChildParentContext } from './CreateConversationPanel.js'
import ConversationSearchDrawer from './ConversationSearchDrawer.js'

interface ParentDetail {
  id: string
  referenceId: string
  subject: string | null
  description: string | null
  clientId: string | null
  displayName: string | null
  primaryHandle: string | null
  campaignId: string | null
  assignedTeamId: string | null
  priorityId: string | null
  tags: string[] | null
  // Fixed classification properties
  typeId: string | null
  categoryId: string | null
  subcategoryId: string | null
  statusId: string | null
  impactId: string | null
  urgencyId: string | null
  complexityId: string | null
  locationId: string | null
  branchId: string | null
  departmentId: string | null
  // Additional properties
  additionalProperties: Record<string, unknown>
}

interface InboxAddChildFlowProps {
  /** The parent conversation; null keeps the flow closed. */
  conversationId: string | null
  referenceId?: string | undefined
  onClose: () => void
  /** Called after a child was created or linked. */
  onDone: () => void
}

type Step = 'choose' | 'create' | 'link' | null

export default function InboxAddChildFlow({ conversationId, referenceId, onClose, onDone }: InboxAddChildFlowProps): JSX.Element | null {
  const toast = useToast()
  const [step, setStep] = useState<Step>(null)
  const [parent, setParent] = useState<ParentDetail | null>(null)

  useEffect(() => {
    if (!conversationId) { setStep(null); setParent(null); return }
    setStep('choose')
    let cancelled = false
    api.get<{ conversation: ParentDetail }>(`/conversations/${conversationId}`)
      .then((res) => { if (!cancelled) setParent(res.conversation) })
      .catch(() => { if (!cancelled) setParent(null) })
    return () => { cancelled = true }
  }, [conversationId])

  if (!conversationId) return null

  const close = (): void => { setStep(null); onClose() }
  const parentRef = parent?.referenceId ?? referenceId ?? null
  const parentContext: ChildParentContext = {
    conversationId,
    referenceId: parentRef,
    subject: parent?.subject ?? null,
    description: parent?.description ?? null,
    clientId: parent?.clientId ?? null,
    clientName: parent?.displayName ?? null,
    clientEmail: parent?.primaryHandle ?? null,
    campaignId: parent?.campaignId ?? null,
    teamId: parent?.assignedTeamId ?? null,
    priorityId: parent?.priorityId ?? null,
    tags: parent?.tags ?? undefined,
    // Fixed classification properties
    typeId: parent?.typeId ?? null,
    categoryId: parent?.categoryId ?? null,
    subcategoryId: parent?.subcategoryId ?? null,
    statusId: parent?.statusId ?? null,
    impactId: parent?.impactId ?? null,
    urgencyId: parent?.urgencyId ?? null,
    complexityId: parent?.complexityId ?? null,
    locationId: parent?.locationId ?? null,
    branchId: parent?.branchId ?? null,
    departmentId: parent?.departmentId ?? null,
    // Additional properties
    additionalProperties: parent?.additionalProperties ?? {},
  }

  return (
    <>
      <ChildTicketDrawer
        open={step === 'choose'}
        onClose={close}
        parentReferenceId={parentRef}
        onCreateNew={() => setStep('create')}
        onLinkExisting={() => setStep('link')}
      />
      <CreateConversationPanel
        open={step === 'create'}
        onClose={close}
        onCreate={() => {}}
        parent={parentContext}
        onChildCreated={() => {
          close()
          onDone()
          toast.success('Child conversation created')
        }}
      />
      {step === 'link' && (
        <ConversationSearchDrawer
          open
          onClose={close}
          title="Add Existing Conversation"
          subtitle={`Add a child to #${parentRef ?? ''}`}
          info={{
            lead: `Makes conversations that already exist children of #${parentRef ?? ''}.`,
            points: [
              { term: 'What changes', detail: 'Only the parent link. Each conversation keeps its reference, owner, status and SLA clock.' },
              { term: 'Only one parent', detail: 'A conversation that is already a child of another cannot be added — detach it there first.' },
              { term: 'Closing', detail: `#${parentRef ?? ''} cannot be closed until every child under it is closed.` },
              { term: 'Merging', detail: 'Neither side of a parent/child pair can take part in a merge while the relationship exists.' },
            ],
          }}
          excludeIds={[conversationId]}
          // Only tickets that can become a child: not closed, no parent yet.
          baseParams={{ excludeClosed: '1', withoutParent: '1' }}
          confirmLabel="Add as Child"
          onConfirm={async (ids) => {
            let added = 0
            for (const id of ids) {
              try {
                await api.post(`/conversations/${id}/parent`, { parentConversationId: conversationId })
                added++
              } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Could not add child conversation')
              }
            }
            if (added > 0) {
              toast.success(added === 1 ? 'Child conversation added' : `${added} child conversations added`)
              onDone()
            }
            if (added === ids.length) close()
            return added === ids.length
          }}
        />
      )}
    </>
  )
}
